/**
 * annique-issues#397 — Offers spec v1.0, cart "Special offer" panel (R8–R10) and cart reconcile (R15, R16).
 *
 * WHAT IT DOES
 *   1. Reads the cart lines from the hidden [data-dbm-offer-line] markers that snippets/line-item.liquid renders
 *      (the product's dbm.fn_pricing blob, quantity, gift / voucher flags, unit cost).
 *   2. Asks DBM which offers the viewer may see:
 *        GET /apps/consultant/api/extension/offers
 *          -> { signed_in, consultant, offers: [{ kind:"sb"|"co", cuid, sku, price_inc_vat:"79.00",
 *               min_spend_inc_vat:"599.00"|null, cap, from, to, bought, remaining, variant_id, product_title,
 *               image_url, handle }] }
 *      Spend-and-buy ("sb") offers come for everyone; consultant offers ("co") only for a signed-in consultant,
 *      with what she has left. While DBM's Offers:ReleaseEnabled is off the list is EMPTY, so nothing shows.
 *   3. Fills snippets/offer-panel.liquid: each offer, its price, how many units may be added, and an "Add"
 *      button (cart/add.js), or "Spend R X more to buy ..." when the cart does not qualify yet (R9).
 *   4. Reconciles: when the cart holds MORE units of an offer than allowed, it lowers that offer line with
 *      cart/change.js to the allowance and shows why (R15, R16). It only ever touches a line whose product
 *      carries an in-force offer object — never a normal product.
 *
 * ONE RULE, THREE PORTS
 *   The allowance is computed exactly as the checkout gate does it (shopify-app/extensions/offer-gate,
 *   OfferIdentity.Allowance in C#): spend-and-buy = min(99, floor(RSP_other / min), cap), where RSP_other is
 *   Σ quantity × the price in force (dbm.fn_pricing campaign_override_inc_vat when > 0, else retail_inc_vat —
 *   annique-issues#415) over the OTHER lines — not an in-force spend-and-buy line, not
 *   a voucher reward line (_dbm_voucher_id), not a gift (dbm.fn_gift), not a zero-priced line. Consultant offer
 *   = cap − bought. Money is parsed as an exact decimal (integer ten-thousandths), never a binary float. The
 *   shared golden vectors (tests/fixtures/offer_allowance_golden.json) pin this file to the C# and Rust ports.
 *
 * CACHE-SAFE: the panel ships hidden for every visitor and no Liquid `customer` read is involved; who sees what is
 * decided here from the App Proxy answer. GRACEFUL DEGRADE: any error => panel hidden, cart untouched (the
 * checkout gate is the backstop).
 *
 * LOOP-BREAKER: a reconcile only LOWERS a quantity, one change per offer per pass, under an in-flight lock, and at
 * most MAX_RECONCILES_PER_OFFER times per offer per RECONCILE_WINDOW_MS in this tab — after that it stops and leaves
 * it to the checkout gate, so a disagreement can never make the cart thrash.
 *
 * R24 (Offers spec v1.1): on the Special Offers collection (the hidden [data-dbm-offer-collection] marker that
 * sections/main-collection.liquid renders there, page 1 only) a signed-in consultant with units left sees each
 * consultant offer as a product card at the top of the grid, with an "Add" button. Same probe, same graceful
 * degrade; see bootCollection(). When the cart already holds her allowance the button reads "In your cart" and a
 * click adds nothing but shows the limit in the theme's own error toast (the one the product page uses); a refused
 * add shows Shopify's reason the same way. Nothing on the card ever fails silently.
 *
 * annique-issues#416 (Offers spec R19, R20): the consultant BULK ORDER composer
 * (sections/consultant-place-order.liquid) reuses this core through window.DbmOffersCore.bulkRowOffers — the offers
 * one bulk row (one recipient's order) may add, from the same probe and the same allowance(). The row basis is the
 * server's (BulkOrderOfferRules.EvaluateRow): the RSP of that row's other products at the price in force, no offer
 * SKU counted; a consultant offer on ANY row (R20 v1.2, 22 Oct 2026: she pays the batch), within what she has left
 * across the whole batch.
 */
(function () {
  'use strict';
  if (window.__dbmOffersInit) return; // one init even when both cart sections load the script
  window.__dbmOffersInit = true;

  var ENDPOINT = '/apps/consultant/api/extension/offers';
  var NOTICE_KEY = 'dbm_offers_notice';
  var NOTICE_TTL_MS = 30000;
  var RECONCILE_KEY = 'dbm_offers_reconciled';
  var MAX_RECONCILES_PER_OFFER = 3;
  var RECONCILE_WINDOW_MS = 10 * 60 * 1000;
  var SHOP_TIME_ZONE = 'Africa/Johannesburg'; // shop.ianaTimezone (checked 2026-10-14) — the gate's shop.localTime

  // --- offers core (extracted by tests/dbm-offers.test.mjs) ---
  var SCALE = 10000;               // money unit: ten-thousandths of a rand (same as the Rust gate)
  var MAX_SPEND_AND_BUY_UNITS = 99; // R13
  var NO_CUSTOMER_CAP_SENTINEL = 9999;
  var NO_MINIMUM_CEILING = SCALE;  // R1/R2: a minimum of R1 or less is "no minimum"

  // "599.00" / "79.5" / "68.6957" -> exact integer units; anything else (exponent, >4 decimals, junk) -> null.
  function parseMoney(value) {
    if (value == null) return null;
    var v = String(value).trim();
    var neg = false;
    if (v.charAt(0) === '-') { neg = true; v = v.slice(1); }
    var dot = v.indexOf('.');
    var whole = dot >= 0 ? v.slice(0, dot) : v;
    var frac = dot >= 0 ? v.slice(dot + 1) : '';
    if (whole === '' && frac === '') return null;
    if (frac.length > 4 || whole.length > 12 || !/^\d*$/.test(whole) || !/^\d*$/.test(frac)) return null;
    while (frac.length < 4) frac += '0';
    var units = (whole === '' ? 0 : parseInt(whole, 10)) * SCALE + parseInt(frac, 10);
    return neg ? -units : units;
  }

  // R14 — AM "Max per customer": 0, negative and the 9999 sentinel (and above) = no limit (null).
  function customerCap(raw) {
    var n = Number(raw);
    if (!isFinite(n) || Math.floor(n) !== n) return null;
    return n > 0 && n < NO_CUSTOMER_CAP_SENTINEL ? n : null;
  }

  // How many units of an offer the buyer may have; null = no limit. kind: 'sb' | 'co'.
  function allowance(kind, rspOther, min, cap, alreadyBought) {
    if (kind === 'sb') {
      if (min == null || !(min > NO_MINIMUM_CEILING)) return 0;
      if (!(rspOther > 0)) return 0;
      var units = Math.min(Math.floor(rspOther / min), MAX_SPEND_AND_BUY_UNITS);
      return cap == null ? units : Math.max(0, Math.min(units, cap));
    }
    if (kind === 'co') {
      if (cap == null) return null;
      return Math.max(0, cap - Math.max(0, alreadyBought || 0));
    }
    return 0;
  }

  function isIsoDate(s) {
    return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
  }

  // The offer object of a parsed dbm.fn_pricing blob, or null (no offer, or one that does not parse — fail open
  // exactly like the gate: an unparseable offer is not enforced and its line counts as an ordinary product).
  function parseOffer(blob) {
    if (!blob || typeof blob !== 'object') return null;
    var o = blob.offer;
    if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
    var bad = false;
    function text(key) {
      var v = o[key];
      if (v == null) return null;
      if (typeof v !== 'string') { bad = true; return null; }
      var t = v.trim();
      return t === '' ? null : t;
    }
    var k = text('k'), id = text('id'), capText = text('cap'), to = text('to'), minText = text('min');
    if (bad || (k !== 'sb' && k !== 'co') || !id) return null;
    var cap = null;
    if (capText != null) {
      if (!/^-?\d+$/.test(capText)) return null;
      cap = customerCap(parseInt(capText, 10));
    }
    if (to != null && !isIsoDate(to)) return null;
    var min = null;
    if (k === 'sb') {
      if (minText == null) return null;
      min = parseMoney(minText);
      if (min == null || !(min > NO_MINIMUM_CEILING)) return null;
    }
    return { kind: k, min: min, cap: cap, id: id, to: to };
  }

  // In force on `today` (shop calendar day): `to` is the last day, inclusive. No `to` / no usable today = in force.
  function inForce(offer, today) {
    if (!offer.to || !isIsoDate(today)) return true;
    return today <= offer.to;
  }

  // R599 for a whole amount, R79.50 otherwise (the gate's format_rand).
  function formatRand(units) {
    var cents = Math.trunc(units / 100);
    if (cents % 100 === 0) return 'R' + (cents / 100);
    var abs = Math.abs(cents % 100);
    return 'R' + Math.trunc(cents / 100) + '.' + (abs < 10 ? '0' : '') + abs;
  }

  function spendAndBuyMessage(title, min, allowed) {
    return title + ': spend ' + formatRand(min) + ' on other products for each one at the offer price. You may have ' + allowed + '.';
  }
  function notConsultantMessage(title) {
    return title + ' is a consultant offer. Sign in with your consultant account.';
  }
  function consultantLimitMessage(title, cap, bought) {
    return bought > 0
      ? title + ': limited to ' + cap + ' per consultant. You have already bought ' + bought + '.'
      : title + ': limited to ' + cap + ' per consultant.';
  }

  // annique-issues#415 — the per-unit RSP of one line is the price IN FORCE: campaign_override_inc_vat when it is a
  // money string above zero, else retail_inc_vat. That is the figure Shopify carries as variant.price and the cart
  // prints as the consultant's "Total RSP" (snippets/cart-shopper-total.liquid). null = no usable price (the caller
  // falls back to the line's unit cost). Reading retail_inc_vat alone counted a R329 campaign line at R799.
  function unitRsp(blob) {
    if (!blob || typeof blob !== 'object') return null;
    if (typeof blob.campaign_override_inc_vat === 'string') {
      var campaign = parseMoney(blob.campaign_override_inc_vat);
      if (campaign > 0) return campaign;
    }
    if (typeof blob.retail_inc_vat === 'string') {
      var retail = parseMoney(blob.retail_inc_vat);
      if (retail > 0) return retail;
    }
    return null;
  }

  // lines: [{ key, quantity, title, fnPricing (string|null), gift, voucher, unitCost (units) }]
  // -> { rspOther, groups: [{ offer, title, units, lines: [{ key, quantity }] }] }  (the gate's evaluate, minus messages)
  function evaluateCart(lines, today) {
    var rspOther = 0;
    var groups = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var quantity = Math.max(0, Number(line.quantity) || 0);
      var blob = null;
      if (line.fnPricing != null && String(line.fnPricing).trim() !== '') {
        try { blob = JSON.parse(line.fnPricing); } catch (e) { blob = null; }
      }
      var offer = blob ? parseOffer(blob) : null;
      if (offer && !inForce(offer, today)) offer = null;

      if (offer) {
        var g = null;
        for (var j = 0; j < groups.length; j++) { if (groups[j].offer.id === offer.id) { g = groups[j]; break; } }
        if (!g) { g = { offer: offer, title: String(line.title || '').trim(), units: 0, lines: [] }; groups.push(g); }
        g.units += quantity;
        g.lines.push({ key: line.key, quantity: quantity });
        if (offer.kind === 'sb') continue; // R12: the spend-and-buy product never counts toward a minimum
      }

      if (line.voucher || line.gift || !(Number(line.unitCost) > 0)) continue;

      var rsp = unitRsp(blob);
      if (!(rsp > 0)) rsp = Number(line.unitCost);
      rspOther += rsp * quantity;
    }
    return { rspOther: rspOther, groups: groups };
  }

  // How many units of a group the viewer may keep, or { allowed: null } when nothing may be enforced here.
  // viewer: null (DBM did not answer) | { consultant, offers: { "<cuid>": <panel offer> } }
  function groupAllowance(group, rspOther, viewer) {
    var o = group.offer;
    var title = group.title || 'This offer';
    if (o.kind === 'sb') {
      var allowedSb = allowance('sb', rspOther, o.min, o.cap, 0);
      return { allowed: allowedSb, message: spendAndBuyMessage(title, o.min, allowedSb) };
    }
    if (!viewer) return { allowed: null };              // unknown buyer: leave it to the checkout gate
    if (!viewer.consultant) return { allowed: 0, message: notConsultantMessage(title) };
    var p = viewer.offers[o.id];
    if (!p) return { allowed: null };                    // DBM does not list it for her: leave it to the gate
    if (p.remaining == null) return { allowed: null };   // no cap
    var bought = Math.max(0, Number(p.bought) || 0);
    var cap = p.cap == null ? (Number(p.remaining) + bought) : Number(p.cap);
    return { allowed: Math.max(0, Number(p.remaining)), message: consultantLimitMessage(title, cap, bought) };
  }

  // The cart/change.js operations that bring every over-allowance offer down to its allowance (last lines first),
  // and the message for each. Never raises a quantity; never touches a line without an in-force offer.
  function planReconcile(evaluation, viewer) {
    var ops = [];
    for (var i = 0; i < evaluation.groups.length; i++) {
      var g = evaluation.groups[i];
      var a = groupAllowance(g, evaluation.rspOther, viewer);
      if (a.allowed == null || g.units <= a.allowed) continue;
      var excess = g.units - a.allowed;
      var changes = [];
      for (var j = g.lines.length - 1; j >= 0 && excess > 0; j--) {
        var line = g.lines[j];
        var cut = Math.min(line.quantity, excess);
        if (cut <= 0) continue;
        changes.push({ key: line.key, quantity: line.quantity - cut });
        excess -= cut;
      }
      ops.push({ cuid: g.offer.id, changes: changes, removed: g.units - a.allowed, message: a.message });
    }
    return ops;
  }

  // The panel rows: one per offer DBM listed, with what the cart allows now. Consultant offers only reach here for
  // a consultant (DBM never lists them otherwise) and are hidden once she has nothing left (R10).
  function panelRows(evaluation, probe) {
    var rows = [];
    var offers = (probe && probe.offers) || [];
    for (var i = 0; i < offers.length; i++) {
      var p = offers[i];
      if (!p || (p.kind !== 'sb' && p.kind !== 'co') || !p.cuid) continue;
      var inCart = 0;
      for (var j = 0; j < evaluation.groups.length; j++) {
        if (evaluation.groups[j].offer.id === p.cuid) inCart = evaluation.groups[j].units;
      }
      var price = parseMoney(p.price_inc_vat);
      if (price == null) continue;
      var row = { offer: p, price: price, inCart: inCart, canAdd: 0, spendMore: null, limitReached: false };
      if (p.kind === 'sb') {
        var min = parseMoney(p.min_spend_inc_vat);
        if (min == null || !(min > NO_MINIMUM_CEILING)) continue;
        var cap = p.cap == null ? null : customerCap(p.cap);
        var allowed = allowance('sb', evaluation.rspOther, min, cap, 0);
        var ceiling = cap == null ? MAX_SPEND_AND_BUY_UNITS : Math.min(cap, MAX_SPEND_AND_BUY_UNITS);
        row.allowed = allowed;
        row.canAdd = Math.max(0, allowed - inCart);
        if (allowed < ceiling) row.spendMore = min * (allowed + 1) - evaluation.rspOther;
        else row.limitReached = true;
      } else {
        if (p.remaining != null && Number(p.remaining) <= 0) continue; // R10: bought her limit -> not shown
        row.allowed = p.remaining == null ? null : Number(p.remaining);
        row.canAdd = row.allowed == null ? 1 : Math.max(0, row.allowed - inCart);
        row.limitReached = row.allowed != null && inCart >= row.allowed;
      }
      rows.push(row);
    }
    return rows;
  }

  // R24 — the Special Offers page cards. One card per CONSULTANT offer DBM lists for this viewer, only when DBM says
  // she is a consultant, she has units left (remaining > 0, or no cap) and today (shop day) is inside from..to. A
  // spend-and-buy offer never gets a card (R7). Anything malformed is skipped, never guessed.
  function inDateWindow(from, to, today) {
    if (!isIsoDate(today)) return true;              // no usable shop day: DBM already lists only in-force offers
    if (isIsoDate(from) && today < from) return false;
    if (isIsoDate(to) && today > to) return false;   // `to` is the last day, inclusive
    return true;
  }

  function collectionCards(probe, today) {
    var cards = [];
    if (!probe || probe.consultant !== true || !Array.isArray(probe.offers)) return cards;
    for (var i = 0; i < probe.offers.length; i++) {
      var p = probe.offers[i];
      if (!p || p.kind !== 'co' || !p.cuid) continue;
      if (p.remaining != null && !(Number(p.remaining) > 0)) continue; // bought her limit (R10, R24)
      if (!inDateWindow(p.from, p.to, today)) continue;
      var price = parseMoney(p.price_inc_vat);
      if (price == null || !(price > 0)) continue;
      if (!p.variant_id || !/^\d+$/.test(String(p.variant_id))) continue;
      if (!p.handle || !/^[a-z0-9][a-z0-9-]*$/.test(String(p.handle))) continue;
      var cap = p.cap == null ? null : customerCap(p.cap);
      var remaining = p.remaining == null ? null : Number(p.remaining);
      var bought = Math.max(0, Number(p.bought) || 0);
      cards.push({
        cuid: String(p.cuid),
        title: (p.product_title && String(p.product_title).trim()) || String(p.sku || ''),
        handle: String(p.handle),
        variantId: String(p.variant_id),
        imageUrl: typeof p.image_url === 'string' && p.image_url ? p.image_url : null,
        price: price,
        limitLabel: cap == null ? null : cap + ' per consultant',
        allowance: remaining,                                         // units she may still have; null = no cap
        cap: cap != null ? cap : (remaining == null ? null : remaining + bought),
        bought: bought,
      });
    }
    return cards;
  }

  // Units of one variant in a cart.js answer (all its lines); null when the answer is not a cart.
  function cartUnitsOf(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var units = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it && String(it.variant_id != null ? it.variant_id : it.id) === String(variantId)) units += Math.max(0, Number(it.quantity) || 0);
    }
    return units;
  }

  // True when the cart already holds every unit the card allows. No cap (allowance null) is never full.
  function cardFull(card, inCart) {
    return card.allowance != null && inCart != null && inCart >= card.allowance;
  }

  // The card button's label for a cart state.
  function cardLabel(card, inCart) {
    return cardFull(card, inCart) ? 'In your cart' : 'Add';
  }

  // A card price exactly as snippets/price-list.liquid prints one: rounded UP to the whole rand, shop format
  // "R {{amount}}" with a comma thousands separator and the ".00" dropped -> "R 729", "R 1,399".
  function cardMoney(units) {
    var rand = Math.ceil(units / SCALE);
    return 'R ' + String(rand).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // The shop's calendar day (Africa/Johannesburg) as YYYY-MM-DD — what the gate reads as shop.localTime.date.
  function shopToday(now, timeZone) {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', { timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(now);
      var y = '', m = '', d = '';
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'year') y = parts[i].value;
        if (parts[i].type === 'month') m = parts[i].value;
        if (parts[i].type === 'day') d = parts[i].value;
      }
      var s = y + '-' + m + '-' + d;
      return isIsoDate(s) ? s : null;
    } catch (e) { return null; }
  }
  // annique-issues#416 (Offers R19, R20 v1.2) — the offers ONE bulk-order row (one recipient's order) may add.
  //   probe         : the /offers answer ({ consultant, offers: [...] }).
  //   lines         : the row's lines [{ sku, qty, unitRsp }] — unitRsp = the price in force in money units (the
  //                   /price public_price: campaign price when one runs, else RSP), null when not known yet.
  //   otherRowUnits : { "<cuid>": units } the OTHER rows of the batch (her own and her clients') already take of
  //                   each consultant offer.
  // Spend-and-buy (R19): allowance('sb') on the RSP of this row's OTHER products — every line whose SKU is not an
  // offer and has a known price, as the server's BulkOrderOfferRules.EvaluateRow counts them. Consultant offer
  // (R20 v1.2, decided 22 Oct 2026): on ANY row — she pays the batch — up to `remaining` (the ledger, all her
  // orders) less what her other rows take: ONE shared cap. Only for a consultant probe (R10: never retail).
  // -> [{ kind, cuid, sku, title, price, allowed (null = no limit), inRow, canAdd, spendMore, over, min, cap }]
  //    min = the spend-and-buy minimum (money units), cap = "Max per customer" (null = none): the composer's sentences.
  function bulkRowOffers(probe, lines, otherRowUnits) {
    var out = [];
    if (!probe || !Array.isArray(probe.offers)) return out;
    var offerSkus = {};
    for (var i = 0; i < probe.offers.length; i++) {
      var o = probe.offers[i];
      if (o && o.sku) offerSkus[String(o.sku).trim().toUpperCase()] = true;
    }
    var rspOther = 0;
    var units = {};
    for (var j = 0; j < (lines || []).length; j++) {
      var line = lines[j] || {};
      var sku = String(line.sku || '').trim().toUpperCase();
      var qty = Math.max(0, Math.floor(Number(line.qty) || 0));
      if (!sku || !qty) continue;
      if (offerSkus[sku]) { units[sku] = (units[sku] || 0) + qty; continue; } // R12: an offer never counts
      var rsp = Number(line.unitRsp);
      if (rsp > 0) rspOther += rsp * qty;
    }
    var taken = otherRowUnits || {};
    for (var k = 0; k < probe.offers.length; k++) {
      var p = probe.offers[k];
      if (!p || (p.kind !== 'sb' && p.kind !== 'co') || !p.cuid || !p.sku) continue;
      var price = parseMoney(p.price_inc_vat);
      if (price == null || !(price > 0)) continue;
      var pSku = String(p.sku).trim().toUpperCase();
      var inRow = units[pSku] || 0;
      var entry = {
        kind: p.kind, cuid: String(p.cuid), sku: pSku,
        title: (p.product_title && String(p.product_title).trim()) || pSku,
        price: price, allowed: 0, inRow: inRow, canAdd: 0, spendMore: null, over: 0,
        min: null, cap: p.cap == null ? null : customerCap(p.cap),
      };
      if (p.kind === 'sb') {
        var min = parseMoney(p.min_spend_inc_vat);
        if (min == null || !(min > NO_MINIMUM_CEILING)) continue;
        var cap = entry.cap;
        entry.min = min;
        var allowed = allowance('sb', rspOther, min, cap, 0);
        var ceiling = cap == null ? MAX_SPEND_AND_BUY_UNITS : Math.min(cap, MAX_SPEND_AND_BUY_UNITS);
        entry.allowed = allowed;
        if (allowed < ceiling) entry.spendMore = min * (allowed + 1) - rspOther;
      } else {
        if (probe.consultant !== true) {
          if (!inRow) continue;                 // R10: never offered to anyone but a consultant
          entry.allowed = 0;                    // ...and one that is there must go
        } else if (p.remaining == null) {
          entry.allowed = null;                 // no "Max per customer"
        } else {
          entry.allowed = Math.max(0, (Number(p.remaining) || 0) - Math.max(0, Number(taken[String(p.cuid)]) || 0));
          if (entry.allowed <= 0 && !inRow) continue; // R10: bought her limit -> not shown
        }
      }
      entry.canAdd = entry.allowed == null ? 1 : Math.max(0, entry.allowed - inRow);
      entry.over = entry.allowed == null ? 0 : Math.max(0, inRow - entry.allowed);
      out.push(entry);
    }
    return out;
  }
  // --- end offers core ---

  // annique-issues#416 — the bulk-order composer reads the same core (never a copy of the rule).
  window.DbmOffersCore = { parseMoney: parseMoney, formatRand: formatRand, allowance: allowance, bulkRowOffers: bulkRowOffers };

  // ------------------------------------------------------------------------------------------------
  // DOM + network. Everything below degrades to "do nothing" on any error.
  // ------------------------------------------------------------------------------------------------
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function isCartPage() {
    try { return window.themeVariables.settings.pageType === 'cart'; } catch (e) { return false; }
  }

  function readDomLines() {
    var nodes = document.querySelectorAll('[data-dbm-offer-line]');
    var seen = {};
    var lines = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var key = n.getAttribute('data-key');
      if (!key || seen[key]) continue;
      seen[key] = true;
      lines.push({
        key: key,
        quantity: parseInt(n.getAttribute('data-qty'), 10) || 0,
        title: n.getAttribute('data-title') || '',
        fnPricing: n.getAttribute('data-fn-pricing'),
        gift: n.getAttribute('data-gift') === '1',
        voucher: n.getAttribute('data-voucher') === '1',
        unitCost: (parseInt(n.getAttribute('data-cost-cents'), 10) || 0) * 100,
      });
    }
    return lines;
  }

  function readCache(key, ttl) {
    try {
      var c = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!c || typeof c.at !== 'number' || (Date.now() - c.at) >= ttl) return null;
      return c;
    } catch (e) { return null; }
  }
  function writeCache(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value: value })); } catch (e) { /* ignore */ }
  }

  // The answer is per viewer, so it is held IN MEMORY for this page only — never in sessionStorage, where a stale
  // "not a consultant" from before a sign-in could pull a consultant's offer out of her cart. One request per page
  // view that has a cart line; identity cannot change without a page load.
  var probe = null;
  var probePromise = null;

  function fetchProbe() {
    if (probePromise) return probePromise;
    probePromise = fetch(ENDPOINT, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (body && Array.isArray(body.offers)) probe = body;
        else probePromise = null; // a failed answer may be retried on the next cart event
        return probe;
      })
      .catch(function () { probePromise = null; return probe; });
    return probePromise;
  }

  function viewerOf(p) {
    if (!p) return null;
    var map = {};
    for (var i = 0; i < p.offers.length; i++) { if (p.offers[i] && p.offers[i].cuid) map[p.offers[i].cuid] = p.offers[i]; }
    return { consultant: !!p.consultant, offers: map };
  }

  // Loop-breaker ledger: { "<cuid>": [epoch-ms, ...] } of recent reconciles in this tab.
  function mayReconcile(cuid) {
    try {
      var d = JSON.parse(sessionStorage.getItem(RECONCILE_KEY) || '{}') || {};
      var now = Date.now();
      var recent = (d[cuid] || []).filter(function (t) { return now - t < RECONCILE_WINDOW_MS; });
      return recent.length < MAX_RECONCILES_PER_OFFER;
    } catch (e) { return true; }
  }
  function noteReconcile(cuid) {
    try {
      var d = JSON.parse(sessionStorage.getItem(RECONCILE_KEY) || '{}') || {};
      var now = Date.now();
      d[cuid] = (d[cuid] || []).filter(function (t) { return now - t < RECONCILE_WINDOW_MS; }).concat([now]);
      sessionStorage.setItem(RECONCILE_KEY, JSON.stringify(d));
    } catch (e) { /* in-flight lock still protects this pass */ }
  }

  function setNotice(text) { writeCache(NOTICE_KEY, text); }
  function currentNotice() { var c = readCache(NOTICE_KEY, NOTICE_TTL_MS); return c ? c.value : null; }

  function changeLine(key, quantity) {
    return fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  function addVariant(variantId) {
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  // The live cart quantities by line key, or null. Reconcile only acts when the DOM shows exactly these lines, so a
  // stale drawer can never steer a cart/change.js.
  function liveQuantities() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;
        var q = {};
        for (var i = 0; i < cart.items.length; i++) q[cart.items[i].key] = cart.items[i].quantity;
        return q;
      })
      .catch(function () { return null; });
  }

  function announceCartChange() {
    if (isCartPage()) { window.location.reload(); return; } // the cart page renders server-side
    try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) { /* ignore */ }
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function rowText(row, title) {
    var price = formatRand(row.price);
    if (row.offer.kind === 'sb') {
      if (row.canAdd > 0) return 'You may add ' + row.canAdd + ' at ' + price + '.';
      if (row.allowed === 0 && row.spendMore != null) {
        return 'Spend ' + formatRand(row.spendMore) + ' more to buy the ' + title + ' for ' + price + '.';
      }
      if (row.spendMore != null) return 'Spend ' + formatRand(row.spendMore) + ' more to add another one.';
      return 'You have the most allowed in your cart.';
    }
    if (row.canAdd > 0) {
      return row.allowed == null
        ? 'Consultant offer at ' + price + '.'
        : 'Consultant offer: you may add ' + row.canAdd + ' at ' + price + '.';
    }
    return 'You have your consultant offer in your cart.';
  }

  function renderPanels(rows, notice) {
    var panels = document.querySelectorAll('[data-dbm-offer-panel]');
    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var list = panel.querySelector('[data-dbm-offer-panel-list]');
      var msg = panel.querySelector('[data-dbm-offer-panel-message]');
      if (!list || !msg) continue;
      list.textContent = '';
      for (var r = 0; r < rows.length; r++) list.appendChild(renderRow(rows[r]));
      msg.textContent = notice || '';
      msg.hidden = !notice;
      panel.hidden = rows.length === 0 && !notice;
    }
  }

  function renderRow(row) {
    var p = row.offer;
    var title = (p.product_title && String(p.product_title).trim()) || p.sku;
    var item = el('div', 'dbm-offer-panel__item');
    item.setAttribute('data-dbm-offer-cuid', p.cuid);
    if (p.image_url) {
      var img = el('img', 'dbm-offer-panel__image');
      img.src = p.image_url; img.alt = ''; img.loading = 'lazy'; img.width = 56; img.height = 56;
      item.appendChild(img);
    }
    var body = el('div', 'dbm-offer-panel__body');
    body.appendChild(el('p', 'dbm-offer-panel__title', title + ' — ' + formatRand(row.price)));
    body.appendChild(el('p', 'dbm-offer-panel__text', rowText(row, title)));
    item.appendChild(body);
    if (row.canAdd > 0 && p.variant_id && /^\d+$/.test(String(p.variant_id))) {
      var btn = el('button', 'button button--sm dbm-offer-panel__add', 'Add');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Add ' + title + ' to your cart');
      btn.addEventListener('click', function () {
        btn.disabled = true;
        addVariant(p.variant_id).then(function (ok) {
          if (ok) announceCartChange(); else btn.disabled = false;
        });
      });
      item.appendChild(btn);
    }
    return item;
  }

  // ------------------------------------------------------------------------------------------------
  // R24 — consultant-offer cards on the Special Offers collection.
  //
  // sections/main-collection.liquid renders a hidden [data-dbm-offer-collection] marker on that one collection
  // (page 1 only), identical for every visitor. Offer products are seo.hidden (R7), so the server grid never holds
  // them; for a signed-in consultant with units left, cards are built here and put at the TOP of the grid, styled
  // with the classes of the grid's own server-rendered cards so they look native. Nobody else gets a card, a
  // request beyond the one /offers probe, or a layout change. The checkout gate is still the rule; this is a
  // convenience surface.
  //
  // The cards are plain <div class="product-card ...">, not <product-card>: the theme styles the class, and the
  // Item-code sort (assets/collection-sku-sort.js) only moves <product-card> elements, so it never reorders them.
  // ------------------------------------------------------------------------------------------------
  var COLLECTION_MARKER = '[data-dbm-offer-collection]';
  var COLLECTION_STATE_ATTR = 'data-dbm-offer-state';
  var CARD_ATTR = 'data-dbm-offer-card';
  var FALLBACK_CARD_CLASS = 'product-card product-card--info-outside';
  var FALLBACK_IMAGE_CLASS = 'product-card__image product-card__image--primary aspect-square';
  var productJsonCache = {};

  // The normal price for the strike-through, from the offer product's own JSON; null on any failure.
  function compareCents(handle) {
    if (!productJsonCache[handle]) {
      productJsonCache[handle] = fetch(cartRoot() + 'products/' + encodeURIComponent(handle) + '.js',
        { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          var c = prod && Number(prod.compare_at_price);
          return c > 0 ? c : null;
        })
        .catch(function () { return null; });
    }
    return productJsonCache[handle];
  }

  // The live cart (cart.js), or null.
  function readCart() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { return cart && Array.isArray(cart.items) ? cart : null; })
      .catch(function () { return null; });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // The theme's own error toast (<x-toast> in layout/theme.liquid) — the same "toast:show" event the product form
  // sends when cart/add.js refuses, so the card and the product page look the same. The toast inserts the message
  // as HTML, so it is escaped here.
  function showErrorToast(message) {
    try {
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: escapeHtml(message), tone: 'error' } }));
    } catch (e) { /* ignore */ }
  }

  // Add one unit the way the theme's own product form does: bundle the sections that want a re-render (the cart
  // drawer), then announce cart:change as a "variant:add", so the drawer re-renders, opens and the count updates.
  // -> { ok: true, cart|null } or { ok: false, message|null } — a refusal carries Shopify's own reason.
  function addFromCard(variantId) {
    var sections = [];
    try {
      var bundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bundle } }));
      bundle.forEach(function (s) { sections.push(s); });
    } catch (e) { sections = []; }
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sections.length) body.sections = sections.join(',');
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return null; }).then(function (err) {
          var text = err && (err.message || err.description);
          return { ok: false, message: typeof text === 'string' && text.trim() ? text.trim() : null };
        });
      }
      return r.json().catch(function () { return {}; }).then(function (added) {
        var cartType = null;
        try { cartType = window.themeVariables.settings.cartType; } catch (e) { cartType = null; }
        if (cartType === 'page') { window.location.href = cartRoot() + 'cart'; return { ok: true, cart: null }; }
        return readCart()
          .then(function (cart) {
            if (cart && added && added.sections) {
              cart.sections = added.sections;
              document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
            } else {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }
            return { ok: true, cart: cart };
          })
          .catch(function () {
            try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) { /* ignore */ }
            return { ok: true, cart: null };
          });
      });
    }).catch(function () { return { ok: false, message: null }; });
  }

  function cardImageUrl(url) {
    if (!/\/cdn\/|cdn\.shopify\.com/.test(url) || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=600';
  }

  // One entry per card on the page: its button and the units of its variant in the cart (null = not known).
  var cardEntries = [];
  var ADDED_HOLD_MS = 1500;

  function paintCardButton(entry) {
    var label = cardLabel(entry.card, entry.inCart);
    entry.btn.textContent = label;
    entry.btn.setAttribute('aria-label', label === 'Add'
      ? 'Add ' + entry.card.title + ' to your cart'
      : entry.card.title + ' is in your cart');
  }

  // Click: re-read the live cart first. Already holding her allowance -> no add, the limit message in the theme's
  // toast. Otherwise add one; a refused add shows Shopify's reason (or a fallback) the same way — never silent.
  function onCardClick(entry) {
    var btn = entry.btn;
    var card = entry.card;
    if (btn.disabled) return;
    btn.disabled = true;
    readCart().then(function (cart) {
      var live = cartUnitsOf(cart, card.variantId);
      if (live != null) entry.inCart = live;
      if (cardFull(card, entry.inCart)) {
        showErrorToast(consultantLimitMessage(card.title, card.cap, card.bought));
        btn.disabled = false;
        paintCardButton(entry);
        return null;
      }
      return addFromCard(card.variantId).then(function (result) {
        btn.disabled = false;
        if (!result.ok) {
          showErrorToast(result.message || ('We could not add ' + card.title + ' to your cart. Please try again.'));
          paintCardButton(entry);
          return;
        }
        var after = cartUnitsOf(result.cart, card.variantId);
        entry.inCart = after != null ? after : (entry.inCart || 0) + 1;
        entry.holdLabel = true;
        btn.textContent = 'Added';
        setTimeout(function () { entry.holdLabel = false; paintCardButton(entry); }, ADDED_HOLD_MS);
      });
    }).catch(function () { btn.disabled = false; paintCardButton(entry); });
  }

  // After any cart change (drawer edit, reconcile, another tab's add): re-read the cart and relabel every card.
  var cardRefreshPending = null;
  function refreshCardStates() {
    cardEntries = cardEntries.filter(function (e) { return e.btn.isConnected; });
    if (!cardEntries.length) return;
    if (cardRefreshPending) clearTimeout(cardRefreshPending);
    cardRefreshPending = setTimeout(function () {
      cardRefreshPending = null;
      readCart().then(function (cart) {
        if (!cart) return;
        for (var i = 0; i < cardEntries.length; i++) {
          var e = cardEntries[i];
          e.inCart = cartUnitsOf(cart, e.card.variantId);
          if (!e.holdLabel && !e.btn.disabled) paintCardButton(e);
        }
      });
    }, 60);
  }

  function renderCard(card, cardClass, imageClass, inCart) {
    var url = cartRoot() + 'products/' + card.handle;
    var root = el('div', cardClass);
    root.setAttribute(CARD_ATTR, '');
    root.setAttribute('data-dbm-offer-cuid', card.cuid);

    var figure = el('div', 'product-card__figure');
    var badges = el('badge-list', 'badge-list badge-list--column align-start place-self-start');
    var badgeRow = el('span', 'badge-row');
    badgeRow.appendChild(el('span', 'badge badge--custom', 'Consultant offer'));
    if (card.limitLabel) badgeRow.appendChild(el('span', 'badge badge--custom', card.limitLabel));
    badges.appendChild(badgeRow);
    figure.appendChild(badges);
    var media = el('a', 'product-card__media');
    media.href = url;
    if (card.imageUrl) {
      var img = el('img', imageClass);
      img.src = cardImageUrl(card.imageUrl);
      img.alt = card.title;
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      media.appendChild(img);
    }
    figure.appendChild(media);
    root.appendChild(figure);

    var info = el('div', 'product-card__info');
    var stack = el('div', 'v-stack gap-2.5 justify-items-start text-start');
    var titleWrap = el('div', 'v-stack gap-0.5');
    var title = el('a', 'product-title text-sm trim-start', card.title);
    title.href = url;
    titleWrap.appendChild(title);
    stack.appendChild(titleWrap);

    var prices = el('price-list', 'price-list');
    var sale = el('sale-price', card.compare ? 'text-sm text-on-sale' : 'text-sm');
    sale.appendChild(el('span', 'sr-only', 'Offer price'));
    sale.appendChild(document.createTextNode(cardMoney(card.price)));
    prices.appendChild(sale);
    if (card.compare) {
      var was = el('compare-at-price', 'text-sm text-subdued line-through');
      was.appendChild(el('span', 'sr-only', 'Regular price'));
      was.appendChild(document.createTextNode(cardMoney(card.compare)));
      prices.appendChild(was);
    }
    stack.appendChild(prices);

    var btn = el('button', 'button button--sm w-full');
    btn.type = 'button';
    var entry = { card: card, btn: btn, inCart: inCart, holdLabel: false };
    paintCardButton(entry);
    btn.addEventListener('click', function () { onCardClick(entry); });
    cardEntries.push(entry);
    stack.appendChild(btn);
    info.appendChild(stack);
    root.appendChild(info);
    return root;
  }

  function fillCollection(marker) {
    marker.setAttribute(COLLECTION_STATE_ATTR, 'pending');
    var today = shopToday(new Date(), SHOP_TIME_ZONE);
    return fetchProbe().then(function (p) {
      var cards = collectionCards(p, today);
      if (!cards.length) { marker.setAttribute(COLLECTION_STATE_ATTR, 'none'); return null; }
      var lookups = cards.map(function (c) { return compareCents(c.handle); });
      lookups.push(readCart());
      return Promise.all(lookups).then(function (compares) {
        var cart = compares.pop();
        var scope = marker.parentNode;
        var grid = scope && scope.querySelector('product-list');
        if (!grid || !marker.isConnected) { marker.setAttribute(COLLECTION_STATE_ATTR, 'none'); return null; }
        var template = grid.querySelector('product-card');
        var cardClass = (template && template.className) || FALLBACK_CARD_CLASS;
        var templateImage = template && template.querySelector('.product-card__image--primary');
        var imageClass = (templateImage && templateImage.className) || FALLBACK_IMAGE_CLASS;
        var existing = grid.querySelectorAll('[' + CARD_ATTR + ']');
        for (var e = 0; e < existing.length; e++) existing[e].parentNode.removeChild(existing[e]);
        cardEntries = cardEntries.filter(function (x) { return x.btn.isConnected; });
        var first = grid.firstChild;
        for (var i = 0; i < cards.length; i++) {
          var c = cards[i];
          var cents = compares[i];
          // Offer and compare in the same unit (ten-thousandths); only a HIGHER normal price is struck through.
          c.compare = cents != null && cents * 100 > c.price ? cents * 100 : null;
          grid.insertBefore(renderCard(c, cardClass, imageClass, cartUnitsOf(cart, c.variantId)), first);
        }
        marker.setAttribute(COLLECTION_STATE_ATTR, 'shown');
        return null;
      });
    }).catch(function () { marker.setAttribute(COLLECTION_STATE_ATTR, 'none'); });
  }

  function scanCollection() {
    var markers = document.querySelectorAll(COLLECTION_MARKER);
    for (var i = 0; i < markers.length; i++) {
      if (!markers[i].getAttribute(COLLECTION_STATE_ATTR)) fillCollection(markers[i]);
    }
    return markers.length;
  }

  function bootCollection() {
    if (!scanCollection()) return; // not the Special Offers page: no request, no observer
    // A filter or sort change swaps the section HTML in (a fresh, unfilled marker and grid); fill it again from
    // the probe already held in memory.
    if (typeof MutationObserver === 'function') {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; scanCollection(); }, 0);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  var inFlight = false;

  // Cart on every device: while a saved-cart restore holds window.DBMCartLock, start after it instead of
  // racing it (snippets/dbm-cart-sync-config.liquid). Free cart, or no lock on the page: run now, unchanged.
  function deferWhileCartLocked(fn) {
    var lock = window.DBMCartLock;
    if (lock && typeof lock.busy === 'function' && lock.busy()) { lock.whenIdle(fn); return true; }
    return false;
  }

  function run() {
    if (deferWhileCartLocked(run)) return;
    if (inFlight) return;
    var lines = readDomLines();
    var panels = document.querySelectorAll('[data-dbm-offer-panel]');
    if (!lines.length || !panels.length) { renderPanels([], currentNotice()); return; }
    inFlight = true;
    var today = shopToday(new Date(), SHOP_TIME_ZONE);
    fetchProbe().then(function (p) {
      var evaluation = evaluateCart(lines, today);
      renderPanels(panelRows(evaluation, p), currentNotice());
      var ops = planReconcile(evaluation, viewerOf(p)).filter(function (op) { return op.changes.length && mayReconcile(op.cuid); });
      if (!ops.length) return null;
      return liveQuantities().then(function (live) {
        if (!live) return null;
        var liveKeys = Object.keys(live);
        if (liveKeys.length !== lines.length) return null;
        for (var i = 0; i < lines.length; i++) {
          if (live[lines[i].key] !== lines[i].quantity) return null; // DOM is stale: wait for the re-render
        }
        var chain = Promise.resolve(false);
        var changed = false;
        var messages = [];
        ops.forEach(function (op) {
          noteReconcile(op.cuid);
          messages.push(op.message);
          op.changes.forEach(function (c) {
            chain = chain.then(function () { return changeLine(c.key, c.quantity); })
              .then(function (ok) { if (ok) changed = true; });
          });
        });
        return chain.then(function () {
          if (!changed) return null;
          setNotice('We changed your cart. ' + messages.join(' '));
          announceCartChange();
          return null;
        });
      });
    }).catch(function () { /* graceful degrade */ }).then(function () { inFlight = false; });
  }

  var pending = null;
  function schedule() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () { pending = null; run(); }, 60);
  }

  function boot() {
    try { bootCollection(); } catch (e) { /* graceful degrade: no cards */ }
    schedule();
    document.addEventListener('cart-drawer:refreshed', schedule);
    document.addEventListener('cart:change', schedule);
    // R24 cards: relabel "Add" / "In your cart" whenever the cart changes. A no-op off the Special Offers page.
    document.addEventListener('cart-drawer:refreshed', refreshCardStates);
    document.addEventListener('cart:change', refreshCardStates);
    document.addEventListener('cart:refresh', refreshCardStates);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
